import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Menu, RefreshCw, Store, Trash2 } from "lucide-react";
import type { DouyinExperienceCategory } from "@ailan/contracts";
import { clearAllDemoData, getDouyinExperienceDashboard, syncDouyinExperience } from "../api/client";
import { ExperiencePlatformTabs } from "../components/ExperiencePlatformTabs";

const sourceLabels = { DEMO: "演示数据", LIVE: "浏览器实采", CONNECTOR: "企业同步" } as const;

function formatDateTime(value: string) {
  if (!value || value.startsWith("1970-")) return "尚未采集";
  return new Intl.DateTimeFormat("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(value));
}

function ChangeDetails({ change, previousScore, reason }: { change: string | null; previousScore: number | null; reason: string | null }) {
  return (
    <details className="douyinExperienceChange">
      <summary>{change ? `较前一日 ${change}` : "暂无前日数据"}</summary>
      {previousScore !== null && <span>前日得分：{previousScore} 分</span>}
      {reason && <em>分析：{reason}</em>}
    </details>
  );
}

function CategoryPanel({ category }: { category: DouyinExperienceCategory }) {
  return (
    <section className="douyinExperienceCategory">
      <header>
        <div><h2>{category.name}</h2><span>权重 {category.weight}%</span></div>
        <strong>{category.score}<em> 分</em></strong>
        <span>计入总分 {category.weightedScore} 分</span>
        <ChangeDetails change={category.change} previousScore={category.previousScore} reason={category.changeReason} />
      </header>
      {category.metrics.length ? (
        <div className="douyinExperienceMetricTable">
          <table><thead><tr><th>考核指标</th><th>得分</th><th>店铺表现</th><th>权重</th><th>计入分数</th><th>较前1日</th></tr></thead>
            <tbody>{category.metrics.map((metric) => <tr key={metric.key}><td><strong>{metric.name}</strong><small>{metric.standard}</small></td><td className={metric.warning ? "warningText" : "douyinExperienceGood"}>{metric.score}分</td><td>{metric.value}</td><td>{metric.weight}%</td><td>{metric.weightedScore}分</td><td><ChangeDetails change={metric.change} previousScore={metric.previousScore} reason={metric.changeReason} /></td></tr>)}</tbody>
          </table>
        </div>
      ) : <div className="douyinExperienceCategoryEmpty">当前分类暂无展开指标</div>}
    </section>
  );
}

export function DouyinExperiencePage({ onMenuOpen }: { onMenuOpen: () => void }) {
  const queryClient = useQueryClient();
  const [selectedStoreName, setSelectedStoreName] = useState<string | null>(null);
  const dashboard = useQuery({ queryKey: ["douyin-experience-dashboard"], queryFn: getDouyinExperienceDashboard, refetchInterval: 15_000, refetchOnWindowFocus: true });
  const syncMutation = useMutation({
    mutationFn: syncDouyinExperience,
    onSuccess: (data) => queryClient.setQueryData(["douyin-experience-dashboard"], data),
  });
  const clearMutation = useMutation({ mutationFn: clearAllDemoData, onSuccess: async () => queryClient.invalidateQueries() });

  if (dashboard.isLoading) return <main className="main"><div className="centerState"><RefreshCw className="spin" />正在读取抖音体验分...</div></main>;
  if (dashboard.isError || !dashboard.data) return <main className="main"><div className="centerState errorState"><AlertTriangle />无法读取抖音体验分<button className="button" onClick={() => void dashboard.refetch()}>重新加载</button></div></main>;

  const stores = (() => {
    const connected = dashboard.data.windows.map((item) => ({ id: item.id, storeName: item.storeName, status: "CONNECTED" as const }));
    const connectedNames = new Set(connected.map((item) => item.storeName));
    return [...connected, ...dashboard.data.storeDirectory.filter((item) => !connectedNames.has(item.storeName))];
  })();
  const activeStoreName = selectedStoreName ?? stores[0]?.storeName ?? null;
  const window = dashboard.data.windows.find((item) => item.storeName === activeStoreName);
  const demoCount = dashboard.data.windows.filter((item) => item.source === "DEMO").length;
  return (
    <main className="main experienceMain douyinExperienceMain">
      <header className="topbar"><button className="menuButton" aria-label="打开导航" onClick={onMenuOpen}><Menu /></button><strong>店铺后台体验分</strong><span>抖音商家体验分</span><i className={dashboard.data.sync.enabled ? "liveDot" : "offlineDot"} /><small>{dashboard.data.sync.enabled ? "采集窗口已连接" : "等待采集窗口"}</small></header>
      <div className="experienceContent">
        <ExperiencePlatformTabs current="douyin" />
        <section className="douyinExperienceStoreDirectory">
          <header><div><strong>抖音店铺窗口</strong><span>目录 {dashboard.data.storeDirectory.length} 个店铺，已连接 {stores.filter((item) => item.status === "CONNECTED").length} 个</span></div><small>当前只展示已采集到的真实分数</small></header>
          <label className="douyinExperienceStoreSelect"><Store /><span>店铺</span><select value={activeStoreName ?? ""} onChange={(event) => setSelectedStoreName(event.target.value || null)} disabled={!stores.length}>{stores.length ? stores.map((store) => <option key={store.id} value={store.storeName}>{store.storeName}{store.status === "PENDING" ? " · 待采集" : " · 已连接"}</option>) : <option value="">暂无店铺目录</option>}</select></label>
        </section>
        <section className="experienceHead">
          <div><h1>抖音商家体验分</h1><p>{activeStoreName ?? "暂未选择店铺"} · {window ? `更新至 ${formatDateTime(dashboard.data.lastSyncedAt)}` : "待采集真实数据"}</p></div>
          <div className="experienceActions">
            <button className="button dangerButton" disabled={!demoCount || clearMutation.isPending} onClick={() => {
              if (confirm("只清除演示数据，浏览器实采和企业同步数据都会保留。确认继续？")) clearMutation.mutate();
            }}><Trash2 />清除全部演示数据</button>
            <button className="button primary douyinExperiencePrimary" disabled={syncMutation.isPending} onClick={() => syncMutation.mutate()}><RefreshCw className={syncMutation.isPending ? "spin" : ""} />{syncMutation.isPending ? "采集中..." : "同步真实数据"}</button>
          </div>
        </section>
        <section className={`experienceSyncBand ${dashboard.data.sync.enabled ? "connected" : ""}`}>
          {dashboard.data.sync.enabled ? <CheckCircle2 /> : <AlertTriangle />}<div><strong>{dashboard.data.sync.enabled ? "抖店体验分采集运行中" : "尚未连接抖店体验分窗口"}</strong><span>{dashboard.data.sync.message}</span></div><span>每分钟自动采集</span>
        </section>
        {window ? <>
          <section className="douyinExperienceStore"><Store /><div><strong>{window.storeName}</strong><span>{window.industry ? `考核行业：${window.industry}` : window.accountName}{window.validOrderCount !== null ? ` · 近30天有效订单 ${window.validOrderCount.toLocaleString("zh-CN")}单` : ""}</span></div><em className={window.source.toLowerCase()}>{sourceLabels[window.source]}</em><small>平台更新时间：{window.updatedAt}</small></section>
          <section className="douyinExperienceScoreBand">
            <article className="overall"><span>我的体验分</span><strong>{window.overallScore}<em> 分</em></strong><small>消费者展示：{window.consumerRating?.toFixed(1) ?? "--"}</small><ChangeDetails change={window.overallChange} previousScore={window.previousScore} reason={window.changeReason} /></article>
            {window.categories.map((category) => <article key={category.key}><span>{category.name}得分</span><strong>{category.score}<em> 分</em></strong><small>×{category.weight}% = {category.weightedScore}分</small><ChangeDetails change={category.change} previousScore={category.previousScore} reason={category.changeReason} /></article>)}
            <article className="deduction"><span>差行为扣分</span><strong>{window.misconductDeduction}<em> 分</em></strong><small>单独计算扣分</small></article>
          </section>
          {window.categories.map((category) => <CategoryPanel key={category.key} category={category} />)}
        </> : <section className="experienceEmpty"><Store /><strong>{activeStoreName ?? "还没有抖音体验分数据"}</strong><span>该店铺已加入目录，连接对应抖店窗口后才会展示真实分数</span></section>}
      </div>
    </main>
  );
}
